import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UrlSignature } from '../common/apiEndpoint';
import { SuperloginService } from '../superlogin/superlogin.service';

interface MonthStat {
  year: number;
  month: number;
  monthName: string;
  totalSignups: number;
  paidSignups: number;
  pendingSignups: number;
  ghanaianSignups: number;
  internationalSignups: number;
  verifiedSignups: number;
  totalAmountCollected: number;
}

interface ApiResponse {
  data: MonthStat[];
  success: boolean;
  count: number;
}

type MetricKey = 'totalSignups' | 'paidSignups' | 'pendingSignups' | 'ghanaianSignups' | 'internationalSignups' | 'verifiedSignups';

@Component({
  selector: 'app-newsignupsmonthlygraph',
  templateUrl: './newsignupsmonthlygraph.component.html',
  styleUrls: ['./newsignupsmonthlygraph.component.css']
})
export class NewsignupsmonthlygraphComponent implements OnInit {

  titleCase(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

  data: MonthStat[] = [];
  isLoading = false;
  errorMessage = '';
  hoveredIndex: number | null = null;

  activeMetric: MetricKey = 'totalSignups';

  readonly metrics: { key: MetricKey; label: string; color: string }[] = [
    { key: 'totalSignups',         label: 'Total',         color: '#630e12' },
    { key: 'paidSignups',          label: 'Paid',          color: '#16a34a' },
    { key: 'pendingSignups',       label: 'Pending',       color: '#d97706' },
    { key: 'ghanaianSignups',      label: 'Ghanaian',      color: '#0369a1' },
    { key: 'internationalSignups', label: 'International', color: '#7c3aed' },
    { key: 'verifiedSignups',      label: 'Verified',      color: '#0891b2' },
  ];

  readonly svgWidth  = 700;
  readonly svgHeight = 260;
  readonly padL = 44;
  readonly padR = 24;
  readonly padT = 24;
  readonly padB = 56;

  skeletonBars = [1, 2, 3, 4, 5];

  constructor(
    private http: HttpClient,
    private urls: UrlSignature,
    private authService: SuperloginService
  ) {}

  ngOnInit(): void { this.loadData(); }

  getHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Authorization': 'Bearer ' + (this.authService.getToken() || '') });
  }

  loadData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.http.get<ApiResponse>(this.urls.monthlySignupStatsUrl, { headers: this.getHeaders() })
      .subscribe({
        next: (res) => {
          if (!res.success) { this.errorMessage = 'Server returned unsuccessful response.'; this.isLoading = false; return; }
          this.data = res.data || [];
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load monthly stats.';
          this.isLoading = false;
        }
      });
  }

  setMetric(key: MetricKey): void {
    this.activeMetric = key;
    this.hoveredIndex = null;
  }

  get activeMetricMeta() { return this.metrics.find(m => m.key === this.activeMetric)!; }
  get chartValues(): number[] { return this.data.map(d => d[this.activeMetric] as number); }
  get maxValue(): number { return Math.max(...this.chartValues, 1); }

  get yTicks(): number[] {
    const max = this.maxValue;
    const step = Math.ceil(max / 4) || 1;
    const ticks = [];
    for (let i = 0; i <= 4; i++) ticks.push(step * i);
    return ticks;
  }

  chartW(): number { return this.svgWidth - this.padL - this.padR; }
  chartH(): number { return this.svgHeight - this.padT - this.padB; }

  // X position for data point i
  pointX(i: number): number {
    if (this.data.length === 1) return this.padL + this.chartW() / 2;
    return this.padL + (i / (this.data.length - 1)) * this.chartW();
  }

  // Y position for data point i
  pointY(i: number): number {
    return this.padT + this.chartH() - (this.chartValues[i] / this.maxValue) * this.chartH();
  }

  // Y position for a tick
  yTickY(tick: number): number {
    return this.padT + this.chartH() - (tick / this.maxValue) * this.chartH();
  }

  // SVG polyline points string
  get linePoints(): string {
    return this.data.map((_, i) => `${this.pointX(i)},${this.pointY(i)}`).join(' ');
  }

  // Smooth cubic bezier path
  get linePath(): string {
    if (this.data.length === 0) return '';
    if (this.data.length === 1) {
      const x = this.pointX(0); const y = this.pointY(0);
      return `M ${x} ${y}`;
    }
    let d = `M ${this.pointX(0)} ${this.pointY(0)}`;
    for (let i = 1; i < this.data.length; i++) {
      const x0 = this.pointX(i - 1), y0 = this.pointY(i - 1);
      const x1 = this.pointX(i),     y1 = this.pointY(i);
      const cpx = (x0 + x1) / 2;
      d += ` C ${cpx},${y0} ${cpx},${y1} ${x1},${y1}`;
    }
    return d;
  }

  // Area fill path (line path + close to bottom)
  get areaPath(): string {
    if (this.data.length === 0) return '';
    const base = this.padT + this.chartH();
    return `${this.linePath} L ${this.pointX(this.data.length - 1)},${base} L ${this.pointX(0)},${base} Z`;
  }

  formatMonthLabel(d: MonthStat): string {
    return d.monthName.substring(0, 3) + ' \'' + String(d.year).substring(2);
  }

  onPointHover(i: number): void { this.hoveredIndex = i; }
  onChartLeave(): void { this.hoveredIndex = null; }

  get totalRevenue(): number { return this.data.reduce((s, d) => s + d.totalAmountCollected, 0); }
  get totalAll(): number { return this.data.reduce((s, d) => s + d.totalSignups, 0); }

  // Tooltip clamp so it doesn't overflow edges
  tooltipX(i: number): number {
    const x = this.pointX(i);
    return Math.min(Math.max(x - 68, this.padL), this.svgWidth - this.padR - 136);
  }
  tooltipY(i: number): number {
    const y = this.pointY(i);
    return y - 80 < this.padT ? y + 14 : y - 80;
  }
}